import React, { useState, useEffect, useRef } from 'react';
import './Contact.css';
import Footer from '../../components/Footer';
import BannerLogo from '../../components/BannerLogo';
import NextPageCTA from '../../components/NextPageCTA';
import contactUsImage from '../../assets/images/contactus.webp';
import { useEnquirySubmit } from '../../hooks/useEnquirySubmit.ts';
import { securityConfig } from '../../config.js';
import {
  validateEnquiryForm,
  hasValidationErrors,
} from '../../lib/formValidation';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullname: '',
    phone: '',
    organization: '', 
    title: '',
    email: '',
    subject: '',
    otherSubject: '',
    message: ''
  });
  
  const [formSubmitStatus, setFormSubmitStatus] = useState({
    success: '',
    error: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedCards, setAnimatedCards] = useState(false);
  const [submittedOptimistically, setSubmittedOptimistically] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  // Refs for scroll animation elements
  const contactInfoRef = useRef(null);
  const contactFormRef = useRef(null);
  const mapRef = useRef(null);
  
  useEffect(() => {
    setIsLoaded(true);
    setIsVisible(true);
    
    // Animate cards with staggered delay
    setTimeout(() => {
      setAnimatedCards(true);
    }, 500);
    
    // Set up intersection observer for scroll animations
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const handleIntersect = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          observer.unobserve(entry.target);
        }
      });
    };
    
    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    
    // Observe all elements with the animate-on-scroll class
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      observer.observe(el);
    });

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    setFormData({
      ...formData,
      [name]: value,
    });
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (formSubmitStatus.error) {
      setFormSubmitStatus({ success: '', error: '' });
    }
  };

  const { submitEnquiry, isSubmitting } = useEnquirySubmit({
    onOptimisticSubmit: () => {
      setSubmittedOptimistically(true);
      setFormSubmitStatus({ success: '', error: '' });
    },
    onCommitted: () => {
      setFormData({
        fullname: '',
        phone: '',
        organization: '',
        title: '',
        email: '',
        subject: '',
        otherSubject: '',
        message: ''
      });
      setFieldErrors({});
      setSubmittedOptimistically(false);
      setShowModal(true);
    },
    onRollback: (previousFormData) => {
      setSubmittedOptimistically(false);
      if (previousFormData) {
        setFormData(previousFormData);
      }
      setFormSubmitStatus({ success: '', error: 'Could not submit enquiry. Please try again.' });
    },
    onNotificationFailed: (data) => {
      setSubmittedOptimistically(false);
      const hint = data?.mail_failure_hint;
      let errMsg =
        'Your enquiry was received, but we could not send the notification email. Please try again or call +91 40 3517 1118.';
      if (hint === 'MAIL_NOT_CONFIGURED') {
        errMsg =
          'The API server is not set up to send email. Please call +91 40 3517 1118.';
      } else if (
        hint === 'GRAPH_AUTH_FAILED' ||
        hint === 'GRAPH_FORBIDDEN' ||
        hint === 'GRAPH_INVALID_SENDER'
      ) {
        errMsg =
          'Email could not be sent (Microsoft Graph). Please call +91 40 3517 1118.';
      }
      setFormSubmitStatus({ success: '', error: errMsg });
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowModal(false);
    setFormSubmitStatus({ success: '', error: '' });
    const service = formData.otherSubject === 'Other' ? formData.subject : formData.otherSubject;
    const errors = validateEnquiryForm(formData, {
      nameKey: 'fullname',
      serviceFieldKey: 'otherSubject',
      resolvedService: service,
    });
    if (formData.otherSubject === 'Other' && !formData.subject.trim()) {
      errors.subject = 'Please specify the service.';
    }
    if (hasValidationErrors(errors)) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    let captchaToken = '';
    if (securityConfig.turnstileSiteKey && window.turnstile && typeof window.turnstile.execute === 'function') {
      try {
        captchaToken = await window.turnstile.execute(securityConfig.turnstileSiteKey, {
          action: 'contact_form',
        });
      } catch {
        setFormSubmitStatus({
          success: '',
          error: 'Captcha verification failed. Please refresh and try again.',
        });
        return;
      }
    }
    if (securityConfig.turnstileSiteKey && !captchaToken) {
      setFormSubmitStatus({
        success: '',
        error: 'Captcha verification failed. Please refresh and try again.',
      });
      return;
    }

    submitEnquiry({
      formData: { ...formData },
      payload: {
        name: formData.fullname,
        phone_number: formData.phone,
        email: formData.email,
        organization: formData.organization,
        title: formData.title,
        service,
        subject: service,
        message: formData.message,
        captcha_token: captchaToken,
      },
    });
  };
  
  return (
    <div>
      <div className={`contact-page ${isLoaded ? 'loaded' : ''}`}>
        {/* Hero Overlay Section */}
        <div className="contact-hero-section">
          <div className="contact-hero-image">
            <img src={contactUsImage} alt="Contact Us" />
          </div>
          <div className="contact-hero-overlay">
            <div className="container">
              <div className="contact-hero-content">
                <h1 className="contact-heading">Contact Us</h1>
              </div>
            </div>
          </div>
          <div className="hero-logo-wrapper">
            <BannerLogo />
          </div>
        </div>

        {/* Contact Section */}
        <section className={`contact-section ${isVisible ? 'visible' : ''}`}>
          <div className="container">
            {/* Contact Information Cards */}
            <div className="contact-info-section animate-on-scroll" ref={contactInfoRef}>
              <div className={`contact-cards ${animatedCards ? 'animated' : ''}`}>
                <div className="contact-card" style={{animationDelay: '0.1s'}}>
                  <div className="contact-card-icon">
                    <i className="fas fa-map-marker-alt"></i>
                  </div>
                  <h3>Our Location</h3>
                  <p>Vardaan Resources Pvt. Ltd.</p>
                  <p>Aurum, 1st Floor, Plot no 57, Jayabheri Enclave, Phase 2, Gachibowli, Hyderabad - 500032&nbsp;INDIA.</p>
                </div>
                
                <div className="contact-card" style={{animationDelay: '0.3s'}}>
                  <div className="contact-card-icon">
                    <i className="fas fa-phone-volume"></i>
                  </div>
                  <h3>Phone Number</h3>
                  <p>Tel: +91 40 3517 1118</p>
                  <p>Mob: +91 99662 22370</p>
                </div>
                
                <div className="contact-card" style={{animationDelay: '0.5s'}}>
                  <div className="contact-card-icon">
                    <i className="fas fa-envelope-open-text"></i>
                  </div>
                  <h3>Email Us</h3>
                  <p>info@vardaanglobal.com</p>
                </div>
                
                <div className="contact-card" style={{animationDelay: '0.7s'}}>
                  <div className="contact-card-icon">
                    <i className="fas fa-business-time"></i>
                  </div>
                  <h3>Working Hours</h3>
                  <p>8:00 AM - 6:00 PM IST</p>
                </div>
              </div>
            </div>
          
            {/* Map and Contact Form Section - Side by Side */}
            <div className="map-form-section animate-on-scroll">
              <div className="row">
                <div className="col-md-6" ref={mapRef}>
                  {/* Map */}
                  <div className="map-wrapper">
                    <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1373.9325741107368!2d78.35971394864299!3d17.447539946426648!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb935023ea4ec3%3A0x31d6d0829d8aaa5e!2sVardaan%20Resources%20office!5e0!3m2!1sen!2sin!4v1748105508298!5m2!1sen!2sin" 
                      width="100%" 
                      height="100%" 
                      style={{ border: 0 }} 
                      allowFullScreen 
                      loading="lazy" 
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Office Location Map"
                    ></iframe>
                  </div>
                </div>
                
                <div className="col-md-6">
                  {/* Contact Form */}
                  <div className="form-card" ref={contactFormRef}>
                    <div className="form-header">
                      <h3>Contact Us</h3>
                      <p>We'd love to hear from you. Please fill out the form below.</p>
                    </div>
                    
                    {formSubmitStatus.error && (
                      <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        <p>{formSubmitStatus.error}</p>
                      </div>
                    )}
                    <form className="contact-form" onSubmit={handleSubmit} noValidate>
                      <div className="form-row">
                        <div className="form-group">
                          <div className={`input-icon-wrapper${fieldErrors.fullname ? ' input-invalid' : ''}`}>
                            <i className="fas fa-user-circle input-icon"></i>
                            <input 
                              type="text" 
                              className="form-control" 
                              id="fullname"
                              placeholder="Your Name"
                              maxLength={100}
                              name="fullname" 
                              value={formData.fullname}
                              onChange={handleChange}
                              aria-invalid={Boolean(fieldErrors.fullname)}
                              aria-describedby={fieldErrors.fullname ? 'contact-fullname-error' : undefined}
                            />
                          </div>
                          {fieldErrors.fullname && (
                            <span id="contact-fullname-error" className="field-error" role="alert">
                              {fieldErrors.fullname}
                            </span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <div className={`input-icon-wrapper${fieldErrors.phone ? ' input-invalid' : ''}`}>
                            <i className="fas fa-phone-alt input-icon"></i>
                            <input 
                              type="tel" 
                              className="form-control" 
                              id="phone"
                              placeholder="Phone Number (10 digits)"
                              inputMode="numeric"
                              pattern="[0-9]{10}"
                              maxLength={10}
                              name="phone" 
                              value={formData.phone}
                              onChange={handleChange}
                              aria-invalid={Boolean(fieldErrors.phone)}
                              aria-describedby={fieldErrors.phone ? 'contact-phone-error' : undefined}
                            />
                          </div>
                          {fieldErrors.phone && (
                            <span id="contact-phone-error" className="field-error" role="alert">
                              {fieldErrors.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <div className={`input-icon-wrapper${fieldErrors.email ? ' input-invalid' : ''}`}>
                            <i className="fas fa-envelope input-icon"></i>
                            <input 
                              type="email" 
                              className="form-control" 
                              id="email"
                              placeholder="Email Address"
                              maxLength={254}
                              name="email" 
                              value={formData.email}
                              onChange={handleChange}
                              aria-invalid={Boolean(fieldErrors.email)}
                              aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                            />
                          </div>
                          {fieldErrors.email && (
                            <span id="contact-email-error" className="field-error" role="alert">
                              {fieldErrors.email}
                            </span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <div className={`input-icon-wrapper${fieldErrors.organization ? ' input-invalid' : ''}`}>
                            <i className="fas fa-building input-icon"></i>
                            <input 
                              type="text" 
                              className="form-control" 
                              id="organization"
                              placeholder="Your Organization"
                              maxLength={150}
                              name="organization" 
                              value={formData.organization}
                              onChange={handleChange}
                              aria-invalid={Boolean(fieldErrors.organization)}
                              aria-describedby={fieldErrors.organization ? 'contact-organization-error' : undefined}
                            />
                          </div>
                          {fieldErrors.organization && (
                            <span id="contact-organization-error" className="field-error" role="alert">
                              {fieldErrors.organization}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <div className={`input-icon-wrapper${fieldErrors.title ? ' input-invalid' : ''}`}>
                            <i className="fas fa-id-badge input-icon"></i>
                            <input 
                              type="text" 
                              className="form-control" 
                              id="title"
                              placeholder="Your Title"
                              maxLength={100}
                              name="title" 
                              value={formData.title}
                              onChange={handleChange}
                              aria-invalid={Boolean(fieldErrors.title)}
                              aria-describedby={fieldErrors.title ? 'contact-title-error' : undefined}
                            />
                          </div>
                          {fieldErrors.title && (
                            <span id="contact-title-error" className="field-error" role="alert">
                              {fieldErrors.title}
                            </span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <div className={`input-icon-wrapper${fieldErrors.otherSubject ? ' input-invalid' : ''}`}>
                            <i className="fas fa-list-alt input-icon"></i>
                            <select 
                              className="form-control" 
                              id="otherSubject"
                              name="otherSubject" 
                              value={formData.otherSubject}
                              onChange={handleChange}
                              aria-invalid={Boolean(fieldErrors.otherSubject)}
                              aria-describedby={fieldErrors.otherSubject ? 'contact-service-error' : undefined}
                            >
                              <option value="" disabled>Select Service</option>
                              <option value="Capitalization">Capitalization</option>
                              <option value="Valuation">Valuation</option>
                              <option value="Assessment of Useful Life">Assessment of Useful Life</option>
                              <option value="Physical Verification">Physical Verification</option>
                              <option value="Balanced Useful Life">Balanced Useful Life</option>
                              <option value="(EPC)">Engineering, Procurement & Construction - Solar</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          {fieldErrors.otherSubject && (
                            <span id="contact-service-error" className="field-error" role="alert">
                              {fieldErrors.otherSubject}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {formData.otherSubject === "Other" && (
                        <div className="form-row" style={{ marginTop: '5px' }}>
                          <div className="form-group full-width-group" style={{ width: '100%' }}>
                            <div className={`input-icon-wrapper${fieldErrors.subject ? ' input-invalid' : ''}`}>
                              <i className="fas fa-heading input-icon"></i>
                              <input 
                                type="text" 
                                className="form-control" 
                                id="subject"
                                placeholder="Please specify service"
                                maxLength={80}
                                name="subject" 
                                value={formData.subject}
                                onChange={handleChange}
                                aria-invalid={Boolean(fieldErrors.subject)}
                                aria-describedby={fieldErrors.subject ? 'contact-subject-error' : undefined}
                              />
                            </div>
                            {fieldErrors.subject && (
                              <span id="contact-subject-error" className="field-error" role="alert">
                                {fieldErrors.subject}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="form-group">
                        <div className={`input-icon-wrapper${fieldErrors.message ? ' input-invalid' : ''}`}>
                          <i className="fas fa-comment-alt input-icon textarea-icon"></i>
                          <textarea 
                            className="form-control" 
                            id="message"
                            placeholder="Your Message"
                            name="message" 
                            value={formData.message}
                            onChange={handleChange}
                            maxLength={2000}
                            rows={3}
                            aria-invalid={Boolean(fieldErrors.message)}
                            aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                          ></textarea>
                        </div>
                        {fieldErrors.message && (
                          <span id="contact-message-error" className="field-error" role="alert">
                            {fieldErrors.message}
                          </span>
                        )}
                      </div>
                      
                      <div className="button-container">
                        <button type="submit" className="submit-btn" disabled={isSubmitting}>
                          {isSubmitting ? <span className="submit-inline-spinner" aria-hidden="true"></span> : null}
                          <span>{isSubmitting ? 'SUBMITTING...' : (submittedOptimistically ? 'SUBMITTED' : 'SUBMIT')}</span>
                          {!isSubmitting ? <i className="fas fa-paper-plane send-icon"></i> : null}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {showModal && (
          <div className="modal-overlay">
            <div className="success-modal">
              <div className="modal-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Thank You!</h3>
              <p>Your enquiry was submitted successfully. Our team will contact you shortly.</p>
              <p className="response-time">Expected response time: 24-48 hours</p>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Journey Navigation - Next Page CTA */}
      <NextPageCTA 
        title="Visit Again" 
        description="Thank you for exploring our site. Return to our homepage to discover more about how Vardaan Resources can support your engineering needs."
        nextPage={{
          path: "/",
          label: "Return to Home"
        }}
        headingText="RESTART YOUR JOURNEY"
      />
      <Footer />
    </div>
  );
};

export default Contact; 